import { getMenuItems } from '~/utils/dataProviders/AccountApiDataProvider';
import { useAccountStore } from '~/stores/accountStore';
import { useNavigationStore } from '~/stores/navigationStore';
import type { INavItem } from '~/types/types';
import { ECalendarDisplayMode, EMenuType } from '~/types/enums';
import type { RouteLocationNormalized } from 'vue-router';
import { findTreeLineByPath, isCached } from '~/utils/helpers';
import type { MenuItem } from '~/types/adminApi';
import { loadLiteSidebar } from '~/utils/LiteSidebarLoader';
import { navItems as navItemsMock } from '~/server/mocks/navItems';
import { handleRequestWithMock } from '~/composables/handleRequestWithMock';

const SupportedMenuTypes = [
    EMenuType.CUSTOM,
    EMenuType.CUSTOM_PAGE,
    EMenuType.RSS,
    EMenuType.POSTS_PAGE,
    EMenuType.MENU_COLLECTION,
    EMenuType.DASHBOARD,
    EMenuType.EVENT_CALENDAR
];

export async function loadNavItemsWithSidebar(to: RouteLocationNormalized) {
    const navigationStore = useNavigationStore();
    const areNavItemsCached = isCached(navigationStore.state.loadedAt);

    if (areNavItemsCached) {
        setNavigationOnLoad(to, navigationStore);
        return;
    }

    const { id: accountId } = useAccountStore().state;
    const menuItems = await handleRequestWithMock<MenuItem[]>(() => getMenuItems(accountId), navItemsMock);

    navigationStore.setNavItems(
        mapNavItems(menuItems.filter(isMenuItemSupported))
    );

    setNavigationOnLoad(to, navigationStore);
    navigationStore.setLoadedAt(Date.now());
    await loadLiteSidebar();
}

function isMenuItemSupported(menuItem: MenuItem): boolean {
    return isSupportedMenuType(menuItem.type) && hasNotGroupRestriction(menuItem.groupIds);
}

function isSupportedMenuType(itemType: EMenuType): boolean {
    return SupportedMenuTypes.includes(itemType);
}

function hasNotGroupRestriction(itemGroupIds: number[] | null): boolean {
    return (!itemGroupIds || itemGroupIds.length === 0);
}

function makeAppLink(menuItem: MenuItem): string {
    if (menuItem.type === EMenuType.CUSTOM) {
        return menuItem.link;
    }

    if (menuItem.type === EMenuType.CUSTOM_PAGE) {
        return `/p/${menuItem.link}`;
    }

    if (menuItem.type === EMenuType.RSS) {
        return `/r/${menuItem.pageSlug}`;
    }

    if (menuItem.type === EMenuType.POSTS_PAGE) {
        return `/n/${menuItem.pageSlug}`;
    }

    if (menuItem.type === EMenuType.MENU_COLLECTION) {
        return `/m/${menuItem.pageSlug}`;
    }

    if (menuItem.type === EMenuType.EVENT_CALENDAR) {
        let eventCalendarLink = `/${menuItem.link}`;

        if (menuItem.pageSlug) {
            eventCalendarLink = eventCalendarLink.concat(`/${menuItem.pageSlug}`);
        }

        if (menuItem.calendarDisplayMode === ECalendarDisplayMode.CALENDAR) {
            eventCalendarLink = eventCalendarLink.concat('?preview=true');
        }

        return eventCalendarLink;
    }

    if (menuItem.type === EMenuType.DASHBOARD) {
        return '/dashboard';
    }

    return '#';
}

function makeSlug(menuItem: MenuItem): string {
    if (menuItem.type === EMenuType.CUSTOM) {
        return '#' + menuItem.id;
    }

    if (menuItem.type === EMenuType.CUSTOM_PAGE) {
        return menuItem.link;
    }

    if (menuItem.type === EMenuType.RSS) {
        return menuItem.pageSlug;
    }

    if (menuItem.type === EMenuType.POSTS_PAGE) {
        return menuItem.pageSlug;
    }

    if (menuItem.type === EMenuType.MENU_COLLECTION) {
        return menuItem.pageSlug;
    }

    if (menuItem.type === EMenuType.EVENT_CALENDAR) {
        if (menuItem.pageSlug) {
            return menuItem.pageSlug;
        }

        return menuItem.link;
    }

    if (menuItem.type === EMenuType.DASHBOARD) {
        return 'dashboard';
    }

    return '#';
}

function mapNavItems(menuItems: MenuItem[], child= false): INavItem[] {
    return menuItems.map((item: MenuItem, index: number) => ({
        id: item.id,
        label: item.title,
        link: (index === 0 && !child) ? '/' : makeAppLink(item),
        icon: item.customMenuIcon.iconSvg,
        type: item.type,
        orderBy: item.orderBy,
        slug: makeSlug(item),
        items: item.items && item.items.length > 0 ? mapNavItems(item.items.filter(isMenuItemSupported), true) : [],
        rss: item.type === EMenuType.RSS ? item.link : null,
        postGroupIds: item.postGroupIds,
        hiddenHeader: item.hiddenHeader,
        hasArchive: item.hasArchive,
    }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function setNavigationOnLoad(to: RouteLocationNormalized, store: any) {
    const activeItemTreeLine = findTreeLineByPath(store.state.navItems, to.path.replace('/archive', ''));
    if (activeItemTreeLine && activeItemTreeLine.length > 0) {
        store.state.activeNavItems = [];
        activeItemTreeLine.forEach((item: INavItem) => {
            store.state.activeNavItems.push(item);
        });

        const currentNavigationLevel = activeItemTreeLine.length;
        const currentNavItem = activeItemTreeLine[currentNavigationLevel - 1];
        store.setPageDetails(
            currentNavItem.label,
            currentNavItem.description,
            currentNavItem.hiddenHeader,
            currentNavItem.icon,
        );
    } else {
        store.resetPageDetails();
    }
}
